import { CloseOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { Link } from '@remix-run/react'
import {
  Button,
  Card,
  Flex,
  Form,
  FormProps,
  Input,
  Select,
  Switch,
  theme,
  Typography,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { JsonInput } from '~/features/shared/components'
import { FormInputType, StepType } from '../../../common/types'
import { BlurryInput } from './components/BlurryInput'
import { useInputTypeOptions } from './hooks/useInputTypeOptions'

const { useToken } = theme

const FormWrapper = styled(Form<StepType>)`
  .ant-form-item {
    margin-bottom: 8px;
  }
`

type Props = {
  index: number
  step: StepType
  formInput: FormInputType
  onChange: (values: StepType) => void
  onChangeInstant: (values: StepType) => void
  onMoveUp: () => void
  onMoveDown: () => void
}

export const FormInput: React.FC<Props> = ({
  index,
  step,
  formInput,
  onChange,
  onChangeInstant,
  onMoveUp,
  onMoveDown,
}) => {
  const [keyLocal, setKeyLocal] = useState<string>()

  const { token } = useToken()

  const inputTypeOptions = useInputTypeOptions()

  const [form] = Form.useForm<StepType>()

  useEffect(() => {
    form.setFieldsValue(formInput)
    setKeyLocal(formInput.key)
  }, [formInput])

  const getKey = (name: string) => {
    return `${step.key}.${index}.${name}`
  }

  const handleChange: FormProps['onValuesChange'] = (_values, valuesFull) => {
    const inputsUpdated = [...step.inputs]

    inputsUpdated[index] = { ...formInput, ...valuesFull }

    onChange({
      ...step,
      inputs: inputsUpdated,
    })
  }

  const handleClickDelete = () => {
    const inputsUpdated = [...step.inputs]

    inputsUpdated.splice(index, 1)

    onChangeInstant({
      ...step,
      inputs: inputsUpdated,
    })
  }

  return (
    <>
      <Card
        size="small"
        style={{
          borderLeft: `5px solid ${token.colorPrimary}`, // Colored top border
        }}
      >
        <Flex className="w-full" style={{ overflowX: 'hidden' }}>
          <Flex vertical flex={1} style={{ overflowX: 'hidden' }}>
            <FormWrapper
              form={form}
              initialValues={formInput}
              layout="vertical"
              onValuesChange={handleChange}
              style={{ overflowX: 'hidden' }}
            >
              <Flex gap={8} justify="space-between" align="center">
                {/* <Form.Item htmlFor={getKey('key-input')} name="key"> */}
                <Input
                  id={getKey('key-input')}
                  prefix={
                    <Typography.Text type="secondary">
                      <i className="las la-key"></i> {step.key}.
                    </Typography.Text>
                  }
                  value={keyLocal}
                  placeholder="input-key"
                  variant="borderless"
                  onChange={event => setKeyLocal(event.target.value)}
                  onBlur={() =>
                    handleChange(
                      { key: keyLocal },
                      { ...form.getFieldsValue(), key: keyLocal },
                    )
                  }
                />
                {/* </Form.Item> */}

                <Flex gap={16} align="center">
                  <Form.Item htmlFor={getKey('inputType')} name="inputType">
                    <Select
                      id={getKey('inputType')}
                      placeholder="Input type"
                      style={{ minWidth: '200px' }}
                      options={inputTypeOptions}
                    />
                  </Form.Item>

                  <div>
                    <Button
                      icon={<CloseOutlined />}
                      danger
                      type="text"
                      size="small"
                      onClick={handleClickDelete}
                    />
                  </div>
                </Flex>
              </Flex>

              <Form.Item
                htmlFor={getKey('propsFormItem.label')}
                name={['propsFormItem', 'label']}
              >
                <BlurryInput
                  id={getKey('propsFormItem.label')}
                  placeholder="Question?"
                  variant="borderless"
                />
              </Form.Item>

              <Form.Item
                htmlFor={getKey('propsFormInput.placeholder')}
                name={['propsFormInput', 'placeholder']}
              >
                <BlurryInput
                  id={getKey('propsFormInput.placeholder')}
                  placeholder="Placeholder"
                  variant="borderless"
                />
              </Form.Item>

              <Form.Item
                htmlFor={getKey('propsIndication.content')}
                name={['propsIndication', 'content']}
              >
                <BlurryInput
                  id={getKey('propsIndication.content')}
                  placeholder="Indication"
                  variant="borderless"
                />
              </Form.Item>

              <Form.Item
                htmlFor={getKey('propsFormItem.rules')}
                name={['propsFormItem', 'rules', 0, 'required']}
              >
                <Switch
                  id={getKey('propsFormItem.rules')}
                  checkedChildren="Required"
                  unCheckedChildren="Optional"
                />
              </Form.Item>

              <Form.Item
                htmlFor={getKey('ifConditions')}
                name={['ifConditions']}
                label={
                  <Typography.Text type="secondary">
                    IF Conditions using{' '}
                    <Link
                      to="https://www.npmjs.com/package/jexl"
                      target="_blank"
                      rel="noreferrer"
                    >
                      jexl
                    </Link>
                  </Typography.Text>
                }
              >
                <BlurryInput
                  id={getKey('ifConditions')}
                  placeholder="step.input == true"
                  variant="borderless"
                />
              </Form.Item>

              <Form.Item
                htmlFor={getKey('disabledConditions')}
                name={['disabledConditions']}
                label={
                  <Typography.Text type="secondary">
                    Disabled Conditions using{' '}
                    <Link
                      to="https://www.npmjs.com/package/jexl"
                      target="_blank"
                      rel="noreferrer"
                    >
                      jexl
                    </Link>
                  </Typography.Text>
                }
              >
                <BlurryInput
                  id={getKey('disabledConditions')}
                  placeholder="step.input == true"
                  variant="borderless"
                  // autoSize={{ minRows: 1, maxRows: 4 }}
                />
              </Form.Item>

              <Form.Item
                trigger="onBlur"
                htmlFor={getKey('propsFormItem.json')}
                name="propsFormItem"
                label="<Form.Item />"
              >
                <JsonInput id={getKey('propsFormItem.json')} />
              </Form.Item>

              <Form.Item
                name="propsFormInput"
                label="<Input />"
                htmlFor={getKey('propsFormInput.json')}
              >
                <JsonInput id={getKey('propsFormInput.json')} />
              </Form.Item>
            </FormWrapper>
          </Flex>

          <Flex vertical justify="center">
            <Button icon={<UpOutlined />} type="text" onClick={onMoveUp} />
            <Button icon={<DownOutlined />} type="text" onClick={onMoveDown} />
          </Flex>
        </Flex>
      </Card>
    </>
  )
}
